import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Shield, Clock, Euro, AlertTriangle, Phone } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set());

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const handleCall = () => {
    window.location.href = 'tel:0676583038';
  };

  const faqData = [
    {
      category: "Sécurité & Urgence",
      icon: Shield,
      color: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      questions: [
        {
          question: "Que faire en cas de frelon asiatique ?",
          answer: "🚨 N'approchez jamais du nid ! Le frelon asiatique est particulièrement agressif. Gardez vos distances, éloignez les enfants et animaux, et contactez-nous immédiatement au 06 76 58 30 38. Nous intervenons en urgence pour ce type de situation."
        },
        {
          question: "Les frelons peuvent-ils attaquer en groupe ?",
          answer: "Oui, absolument. Quand un frelon se sent menacé, il libère des phéromones d'alarme qui peuvent déclencher une attaque massive de toute la colonie. C'est pourquoi il faut toujours faire appel à des professionnels équipés."
        },
        {
          question: "Vos interventions sont-elles sécurisées ?",
          answer: "Nous utilisons des équipements de protection intégrale (combinaisons, masques, gants renforcés) et des techniques professionnelles. Nos techniciens sont formés aux protocoles de sécurité et possèdent une assurance responsabilité civile professionnelle."
        }
      ]
    },
    {
      category: "Délais & Disponibilité", 
      icon: Clock,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      questions: [
        {
          question: "Intervenez-vous vraiment sous 48h ?",
          answer: "Oui, c'est notre engagement ! Pour les urgences (frelon asiatique, nid accessible), nous intervenons souvent le jour même. Pour les autres situations, maximum 48h. Nous travaillons 7j/7 y compris weekends et jours fériés."
        },
        {
          question: "Travaillez-vous le weekend et jours fériés ?",
          answer: "Absolument ! Les nuisibles ne prennent pas de vacances, nous non plus. Notre équipe est disponible 7j/7, 24h/24 pour les urgences. Les tarifs restent identiques."
        },
        {
          question: "Peut-on avoir un rendez-vous le soir ?",
          answer: "Oui, nous proposons des créneaux en soirée jusqu'à 20h en semaine et weekends. Certaines interventions sont même plus efficaces en fin de journée quand les nuisibles sont moins actifs."
        }
      ]
    },
    {
      category: "Tarifs & Devis",
      icon: Euro,
      color: "text-green-600", 
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      questions: [
        {
          question: "Le devis est-il vraiment gratuit ?",
          answer: "Oui, le devis est entièrement gratuit et sans engagement ! Nous nous déplaçons, évaluons la situation, proposons la solution adaptée et vous remettez un prix ferme. Vous ne payez que si vous acceptez l'intervention."
        },
        {
          question: "Quels sont vos tarifs moyens ?",
          answer: "Nos tarifs débutent à 80€ pour une intervention simple et varient selon la complexité (hauteur, accessibilité, taille du nid). Le frelon asiatique nécessite un traitement spécialisé. Prix définitif communiqué lors du devis gratuit."
        },
        {
          question: "Y a-t-il des frais de déplacement ?",
          answer: "Aucun frais de déplacement sur toute la Gironde ! Le déplacement pour le devis est gratuit, et si vous acceptez l'intervention, aucun supplément n'est appliqué."
        },
        {
          question: "Proposez-vous des facilités de paiement ?",
          answer: "Oui ! Nous acceptons les espèces, chèques, cartes bancaires et virements. Pour les gros montants, nous pouvons proposer un échelonnement sur 2-3 fois sans frais."
        }
      ]
    },
    {
      category: "Types d'intervention",
      icon: AlertTriangle,
      color: "text-yellow-600",
      bgColor: "bg-yellow-50", 
      borderColor: "border-yellow-200",
      questions: [
        {
          question: "Traitez-vous tous types de nids ?",
          answer: "Oui ! Frelons asiatiques et européens, guêpes, bourdons, abeilles. Nous adaptons notre technique selon l'espèce : destruction, déplacement pour les abeilles, traitements spécifiques pour chaque nuisible."
        },
        {
          question: "Pouvez-vous intervenir en hauteur ?",
          answer: "Absolument ! Nous disposons d'échelles télescopiques jusqu'à 12m et travaillons avec des cordistes pour les hauteurs importantes (toitures, grands arbres). Aucune situation n'est impossible."
        },
        {
          question: "Que faites-vous du nid après destruction ?",
          answer: "Nous évacuons systématiquement les résidus du nid pour éviter une re-colonisation. Nettoyage complet de la zone et conseils préventifs pour éviter une nouvelle installation."
        },
        {
          question: "Proposez-vous des traitements préventifs ?",
          answer: "Oui ! Nous installons des pièges préventifs au printemps pour capturer les reines fondatrices, et proposons des traitements de surface pour décourager l'installation de nouveaux nids."
        }
      ]
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Questions <span className="text-yellow-600">fréquentes</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Toutes les réponses à vos questions sur nos interventions, tarifs et méthodes d'action. 
            Une question ? Appelez-nous directement !
          </p>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-12">
          {faqData.map((category, categoryIndex) => {
            const CategoryIcon = category.icon;
            
            return (
              <div key={categoryIndex} className="space-y-4">
                {/* Category Header */}
                <div className={`${category.bgColor} ${category.borderColor} border-l-4 p-6 rounded-r-lg`}>
                  <div className="flex items-center space-x-3">
                    <CategoryIcon className={`h-6 w-6 ${category.color}`} />
                    <h3 className={`text-2xl font-bold ${category.color}`}>
                      {category.category}
                    </h3>
                  </div>
                </div>

                {/* Questions */}
                <div className="space-y-4">
                  {category.questions.map((faq, questionIndex) => {
                    const itemIndex = `${categoryIndex}-${questionIndex}`;
                    const isOpen = openItems.has(itemIndex);
                    
                    return (
                      <Card key={questionIndex} className="overflow-hidden hover:shadow-lg transition-all duration-300">
                        <CardContent className="p-0">
                          <button
                            onClick={() => toggleItem(itemIndex)}
                            className="w-full p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                          >
                            <div className="flex items-center justify-between">
                              <h4 className="text-lg font-semibold text-gray-900 pr-4">
                                {faq.question}
                              </h4>
                              <div className="flex-shrink-0">
                                {isOpen ? (
                                  <ChevronUp className="h-5 w-5 text-gray-500" />
                                ) : (
                                  <ChevronDown className="h-5 w-5 text-gray-500" />
                                )}
                              </div>
                            </div>
                          </button>
                          
                          {isOpen && (
                            <div className="px-6 pb-6 border-t border-gray-100">
                              <div className="pt-4">
                                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                                  {faq.answer}
                                </p>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-2xl p-8 text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-black mb-4">
            Une question spécifique ?
          </h3>
          <p className="text-lg text-black mb-6 opacity-90">
            Notre équipe d'experts est disponible 7j/7 pour répondre à toutes vos questions
          </p>
          <Button
            onClick={handleCall}
            className="bg-black hover:bg-gray-800 text-white font-bold px-8 py-4 text-lg transform hover:scale-105 transition-all duration-200"
          >
            <Phone className="h-5 w-5 mr-2" />
            Appelez le 06 76 58 30 38
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FAQ;